import React, { useState, useEffect } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { ProductType, Region, SearchPreordersRequest } from '../types';
import { triggerManualRefresh } from '../utils/api';

interface Props {
  onSearch: (req: SearchPreordersRequest) => void;
  loading: boolean;
  initialSearch?: SearchPreordersRequest;
}

const REGION_OPTIONS: { label: string; flag: string; value: Region }[] = [
  { label: 'Spain',          flag: '🇪🇸', value: 'ES' },
  { label: 'European Union', flag: '🇪🇺', value: 'EU' },
  { label: 'United Kingdom', flag: '🇬🇧', value: 'UK' },
  { label: 'United States',  flag: '🇺🇸', value: 'US' },
];

const LINE_OPTIONS: { label: string; value: ProductType; desc: string }[] = [
  { value: 'any',          label: 'All Dragon Ball', desc: 'Search across both formats' },
  { value: 'fusion_world', label: 'Fusion World',    desc: 'FB01 – FB11 and starters' },
  { value: 'masters',      label: 'Masters',         desc: 'DBS-B series' },
];

export function SearchPanel({ onSearch, loading, initialSearch }: Props) {
  const isMobile = useIsMobile();
  const [productType, setProductType] = useState<ProductType>(initialSearch?.productType ?? 'any');
  const [query, setQuery]             = useState(initialSearch?.query ?? '');
  const [regions, setRegions]         = useState<Region[]>(initialSearch?.regions ?? ['ES', 'EU']);
  const [newReleasesOnly, setNewReleasesOnly] = useState(initialSearch?.newReleasesOnly ?? false);
  const [refreshing, setRefreshing]   = useState(false);
  const [refreshMsg, setRefreshMsg]   = useState('');

  async function handleManualRefresh() {
    setRefreshing(true);
    setRefreshMsg('');
    try {
      const res = await triggerManualRefresh();
      setRefreshMsg(`Refresh started — scraping ${res.productCount} products + Bandai catalog. Reopen a product in a few minutes to see verified stock.`);
    } catch (err) {
      setRefreshMsg(err instanceof Error ? err.message : 'Failed to start refresh.');
    } finally {
      setRefreshing(false);
    }
  }

  // Pre-fill when navigated from another tab
  useEffect(() => {
    if (!initialSearch) return;
    setProductType(initialSearch.productType);
    setQuery(initialSearch.query ?? '');
    setRegions(initialSearch.regions ?? ['ES', 'EU']);
    setNewReleasesOnly(initialSearch.newReleasesOnly ?? false);
  }, [initialSearch]);

  function toggleRegion(region: Region) {
    setRegions((prev) =>
      prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region],
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSearch({ productType, query: query.trim() || undefined, regions: regions.length ? regions : undefined, newReleasesOnly });
  }

  const allRegions = regions.length === REGION_OPTIONS.length;

  return (
    <form onSubmit={handleSubmit} style={{ ...s.form, maxWidth: isMobile ? '100%' : '680px' }}>
      {/* Product line */}
      <div style={s.fieldset}>
        <label style={s.legend}>Product line</label>
        <div style={s.lineCards}>
          {LINE_OPTIONS.map(({ value, label, desc }) => (
            <button
              key={value}
              type="button"
              style={{ ...s.lineCard, ...(productType === value ? s.lineCardActive : {}) }}
              onClick={() => setProductType(value)}
            >
              <span style={{ fontWeight: 700, fontSize: '14px' }}>{label}</span>
              <span style={{ fontSize: '11px', color: productType === value ? '#f9731690' : '#8a93b0' }}>{desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Query */}
      <div style={s.fieldset}>
        <label style={s.legend} htmlFor="query">Set or product name <span style={{ color: '#8a93b0', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
        <div style={s.inputWrap}>
          <span style={s.inputIcon}>🔍</span>
          <input
            id="query"
            style={s.input}
            type="text"
            placeholder="e.g. FB07, Wish for Shenron, starter deck…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            maxLength={200}
          />
          {query && (
            <button type="button" style={s.clearBtn} onClick={() => setQuery('')}>✕</button>
          )}
        </div>
      </div>

      {/* Regions */}
      <div style={s.fieldset}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <label style={s.legend}>Regions</label>
          <button
            type="button"
            style={s.selectAllBtn}
            onClick={() => setRegions(allRegions ? [] : REGION_OPTIONS.map((r) => r.value))}
          >
            {allRegions ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        <div style={s.regionGrid}>
          {REGION_OPTIONS.map(({ label, flag, value }) => {
            const checked = regions.includes(value);
            return (
              <label
                key={value}
                style={{ ...s.regionCard, ...(checked ? s.regionCardActive : {}) }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleRegion(value)}
                  style={{ display: 'none' }}
                />
                <span style={{ fontSize: '20px' }}>{flag}</span>
                <span style={{ fontWeight: 600, fontSize: '13px' }}>{label}</span>
                <span style={{ fontSize: '11px', color: checked ? '#f9731690' : '#8a93b0', fontWeight: 700 }}>{value}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Toggle */}
      <label style={s.toggleRow}>
        <div
          style={{ ...s.toggle, background: newReleasesOnly ? '#f97316' : '#2e3347' }}
          onClick={() => setNewReleasesOnly((v) => !v)}
        >
          <div style={{ ...s.toggleKnob, transform: newReleasesOnly ? 'translateX(18px)' : 'translateX(2px)' }} />
        </div>
        <div>
          <div style={{ fontSize: '14px', fontWeight: 600, color: '#e8eaf0' }}>Upcoming releases only</div>
          <div style={{ fontSize: '12px', color: '#8a93b0' }}>Skip products that have already been released</div>
        </div>
      </label>

      <button type="submit" style={{ ...s.submitBtn, ...(isMobile ? { alignSelf: 'stretch', width: '100%' } : {}) }} disabled={loading || regions.length === 0}>
        {loading ? (
          <span>Searching across {regions.join(', ')}…</span>
        ) : (
          <span>Search preorders{regions.length > 0 ? ` in ${regions.join(', ')}` : ''}</span>
        )}
      </button>

      {regions.length === 0 && (
        <p style={{ fontSize: '12px', color: '#ef4444', margin: 0 }}>Select at least one region to search.</p>
      )}

      {/* Manual data refresh — triggers the same cron pipeline on demand */}
      <div style={s.refreshBox}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#e8eaf0', marginBottom: '2px' }}>
            Refresh stock & catalog now
          </div>
          <div style={{ fontSize: '11px', color: '#8a93b0', lineHeight: 1.4 }}>
            Auto-runs nightly at 06:00 UTC. Click to re-scrape all stores and the Bandai catalog
            on demand — takes a few minutes; results appear when ready.
          </div>
        </div>
        <button
          type="button"
          style={{ ...s.refreshBtn, ...(refreshing ? s.refreshBtnDisabled : {}) }}
          onClick={handleManualRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Starting…' : '↻ Refresh'}
        </button>
      </div>
      {refreshMsg && (
        <p style={{
          fontSize: '12px',
          color: refreshMsg.toLowerCase().includes('fail') || refreshMsg.toLowerCase().includes('error') ? '#ef4444' : '#22c55e',
          margin: 0,
        }}>{refreshMsg}</p>
      )}
    </form>
  );
}

const s: Record<string, React.CSSProperties> = {
  form: { display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '680px' },
  fieldset: { display: 'flex', flexDirection: 'column' },
  legend: {
    fontSize: '11px', fontWeight: 700, color: '#8a93b0',
    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '10px',
  },
  lineCards: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  lineCard: {
    flex: '1 1 160px', display: 'flex', flexDirection: 'column', gap: '3px', textAlign: 'left',
    padding: '12px 16px', borderRadius: '10px', border: '1.5px solid #2e3347',
    background: '#1a1d27', color: '#e8eaf0', cursor: 'pointer', transition: 'all 0.15s',
  },
  lineCardActive: { borderColor: '#f97316', background: '#f9731615', color: '#f97316' },
  inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  inputIcon: { position: 'absolute', left: '12px', fontSize: '14px', pointerEvents: 'none' },
  input: {
    width: '100%', padding: '11px 40px 11px 36px', background: '#1a1d27',
    border: '1.5px solid #2e3347', borderRadius: '10px', color: '#e8eaf0',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
  },
  clearBtn: {
    position: 'absolute', right: '12px', background: 'none', border: 'none',
    color: '#8a93b0', cursor: 'pointer', fontSize: '13px', padding: '2px',
  },
  regionGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' },
  regionCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
    padding: '14px 10px', borderRadius: '10px', border: '1.5px solid #2e3347',
    background: '#1a1d27', cursor: 'pointer', transition: 'all 0.15s', textAlign: 'center',
    color: '#e8eaf0',
  },
  regionCardActive: { borderColor: '#f97316', background: '#f9731615' },
  selectAllBtn: {
    background: 'none', border: 'none', color: '#3b82f6',
    cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: 0,
  },
  toggleRow: {
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '14px 16px', background: '#1a1d27', border: '1.5px solid #2e3347',
    borderRadius: '10px', cursor: 'pointer',
  },
  toggle: {
    width: '40px', height: '22px', borderRadius: '11px', position: 'relative',
    flexShrink: 0, cursor: 'pointer', transition: 'background 0.2s',
  },
  toggleKnob: {
    position: 'absolute', top: '3px', width: '16px', height: '16px',
    borderRadius: '50%', background: '#fff', transition: 'transform 0.2s',
  },
  submitBtn: {
    padding: '14px 28px', background: '#f97316', color: '#fff', border: 'none',
    borderRadius: '10px', fontWeight: 700, fontSize: '15px', cursor: 'pointer',
    alignSelf: 'flex-start', transition: 'opacity 0.15s',
    opacity: 1,
  },
  refreshBox: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '12px 14px', background: '#1a1d27', border: '1px dashed #2e3347',
    borderRadius: '10px', marginTop: '8px',
  },
  refreshBtn: {
    padding: '8px 16px', background: 'transparent', border: '1px solid #f9731680',
    borderRadius: '8px', color: '#f97316', fontWeight: 700, fontSize: '13px',
    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  },
  refreshBtnDisabled: { opacity: 0.55, cursor: 'not-allowed' },
};
